/**
 * DocBits Notification System - Constants
 *
 * Centralized configuration constants for consistent
 * notification behavior across the DocBits application.
 *
 * @module DocBitsNotification/constants
 */

import type {
  NotificationType,
  NotificationPosition,
  DocBitsNotificationConfig,
} from './types';

/**
 * Default notification icons by type
 */
export const NOTIFICATION_ICONS: Record<NotificationType, string> = {
  success: 'check_circle',
  error: 'error',
  warning: 'warning',
  info: 'info',
  custom: '',
};

/**
 * Default notification colors by type (Quasar colors)
 */
export const NOTIFICATION_COLORS: Record<NotificationType, string> = {
  success: 'positive',
  error: 'negative',
  warning: 'warning',
  info: 'info',
  custom: '',
};

/**
 * Default notification positions by type
 *
 * Based on usage patterns from existing codebase:
 * - Success/Info: top-right (non-intrusive)
 * - Error: top (more prominent)
 * - Warning: top-right (visible but not blocking)
 */
export const DEFAULT_POSITIONS: Record<NotificationType, NotificationPosition> = {
  success: 'top-right',
  error: 'top',
  warning: 'top-right',
  info: 'top-right',
  custom: 'top-right',
};

/**
 * Duration calculation constants
 */
export const DURATION_CONFIG = {
  /** Milliseconds per character for auto-calculation */
  MS_PER_CHARACTER: 60,
  /** Minimum duration (2 seconds) */
  MIN_DURATION: 2000,
  /** Maximum duration (10 seconds) */
  MAX_DURATION: 10000,
  /** Default duration if not auto-calculated (5 seconds) */
  DEFAULT_DURATION: 5000,
  /** Persistent notification duration (never dismiss) */
  PERSISTENT: 0,
} as const;

/**
 * Default service configuration
 */
export const DEFAULT_CONFIG: DocBitsNotificationConfig = {
  defaultPosition: 'top-right',
  durationPerCharacter: DURATION_CONFIG.MS_PER_CHARACTER,
  minDuration: DURATION_CONFIG.MIN_DURATION,
  maxDuration: DURATION_CONFIG.MAX_DURATION,
  enableHistory: true,
  maxHistorySize: 50,
  enableAnalytics: false, // Disabled by default, can be enabled
  enableGrouping: true,
  groupingWindowMs: 3000, // 3 seconds
};

/**
 * Accessibility ARIA live region types by notification type
 */
export const ARIA_LIVE_REGIONS: Record<NotificationType, 'polite' | 'assertive' | 'off'> = {
  success: 'polite',
  error: 'assertive',
  warning: 'assertive',
  info: 'polite',
  custom: 'polite',
};

/**
 * Analytics event name prefixes
 */
export const ANALYTICS_EVENT_PREFIX = 'notification';

/**
 * LocalStorage keys
 */
export const STORAGE_KEYS = {
  HISTORY: 'docbits_notification_history',
  CONFIG: 'docbits_notification_config',
} as const;

/**
 * Notification z-index (above most UI elements)
 */
export const NOTIFICATION_Z_INDEX = 9000;

/**
 * Progress bar configuration
 */
export const PROGRESS_CONFIG = {
  /** Show progress bar for auto-dismiss notifications */
  SHOW_FOR_AUTO_DISMISS: true,
  /** Hide progress bar for persistent notifications */
  HIDE_FOR_PERSISTENT: true,
} as const;

/**
 * Common notification messages (can be translated)
 */
export const COMMON_MESSAGES = {
  SAVE_SUCCESS: 'Changes saved successfully',
  SAVE_ERROR: 'Failed to save changes',
  DELETE_SUCCESS: 'Item deleted successfully',
  DELETE_ERROR: 'Failed to delete item',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  PERMISSION_DENIED: 'Permission denied',
  INVALID_INPUT: 'Invalid input. Please check your entries.',
  SESSION_EXPIRED: 'Session expired. Please log in again.',
  LOADING: 'Loading...',
  PROCESSING: 'Processing...',
} as const;
